/**
 * The build's CONTENT WRITE MODE — which storage shape this editor's writes
 * ask the API for (CONTENT_MODEL_SPEC §5, §6.1; TraceBackend#930).
 *
 * The backend can store content two ways, and since #930 it can be told which:
 *
 *   folder — the content model. A leaf (GLB/image/video/audio) becomes an
 *            asset FOLDER under `trace-content/assets/{assetId}/` with a
 *            generated wrapper `.gltf` document; a `.glz`/`.tracex` bundle is
 *            unzipped into it, and `metadata.behaviors` is stripped (behaviours
 *            live in the document, spec §3.6). Scenes publish the folder form:
 *            files through `POST /scenes/{id}/files`, the document as
 *            RootContent.
 *   legacy — what the editor wrote BEFORE the content model. The leaf or
 *            bundle goes raw to `trace-cms-assets/{guid}.{ext}` and the row
 *            points at that blob; `metadata.behaviors` is stored as sent;
 *            scenes publish the `.zip` RootContent.
 *
 * Why a switch and not a cutover. Production's API is still TraceBackend
 * `main` (#885) and has no content-model endpoints at all, while the deployed
 * sandbox writes folder scenes — so prod saves fail. Dev has the endpoints but
 * also has the Unity app and the other legacy clients on it, and they read
 * back only what they themselves wrote. So a deployed sandbox writes LEGACY
 * (this default) and developers run folder mode locally with
 * `VITE_CONTENT_STORAGE=folder` in `.env.local`.
 *
 * **Readers accept BOTH shapes in either mode, forever.** The switch only
 * decides what we write — format detection on load, the dropzone accept lists
 * and the importers are all mode-independent and stay that way.
 *
 * Read once at module load like every other `VITE_` setting (see
 * `src/ai/gateway.ts`, `src/cms/CmsManager.ts`), so a change needs a
 * dev-server restart.
 */

/** The build's write mode. */
export type ContentStorage = 'legacy' | 'folder';

/**
 * The API's spelling of the same choice — the value of the optional
 * `contentStorage` multipart field on `POST /assets` and
 * `PATCH /assets/{assetId}` (TraceBackend#930). An unrecognised value is a
 * model-binding failure and answers 400, so it is typed, never free text.
 */
export type ContentStorageApiValue = 'Legacy' | 'Folder';

/**
 * Resolve a raw `VITE_CONTENT_STORAGE` value.
 *
 * Case-insensitive, whitespace-tolerant; unset, empty and unknown all fall
 * back to `'legacy'` — the safe end of the switch, because a legacy write is
 * readable by every client and on every environment, and a folder write is
 * neither. An unknown value warns rather than throwing: a mistyped env var
 * must not take the editor down, but it must not pass silently either.
 *
 * Called exactly ONCE, at module load below, which is what makes the warning
 * a once-per-session boot message. Exported for its own tests.
 */
export function resolveContentStorage(raw: string | undefined | null): ContentStorage {
    const value = (raw ?? '').trim().toLowerCase();
    if (value === 'folder') return 'folder';
    if (value === 'legacy' || value === '') return 'legacy';
    console.warn(
        `[ContentStorage] VITE_CONTENT_STORAGE="${raw}" is not a recognised content storage `
        + `mode ('legacy' or 'folder'); falling back to 'legacy'.`,
    );
    return 'legacy';
}

/** This build's content write mode. */
export const CONTENT_STORAGE: ContentStorage = resolveContentStorage(
    import.meta.env.VITE_CONTENT_STORAGE as string | undefined,
);

/** True when this build writes the content model's folder shape. */
export const IS_FOLDER_STORAGE: boolean = CONTENT_STORAGE === 'folder';

/**
 * The value to send as the `contentStorage` field on an asset write, for
 * everything that is not a `.gltf` document (see
 * `src/cms/asset-writes.ts` for the document exception).
 */
export const CONTENT_STORAGE_API_VALUE: ContentStorageApiValue = IS_FOLDER_STORAGE
    ? 'Folder'
    : 'Legacy';
